import { z } from 'zod';

export const playableBaseCapabilityIds = [
  'layers.container',
  'layers.sprite',
  'layers.text',
  'input.pointer',
  'input.tap',
  'input.swipe',
  'input.drag',
  'loop.raf',
  'loop.delay',
  'loop.interval',
  'tween.basic',
  'slots.ref',
  'assets.alias',
  'telemetry.emit',
  'cta.open',
] as const;

export const unsupportedAdvancedCapabilityIds = [
  'shader.custom',
  'physics.engine',
  'renderer.custom',
] as const;

export const advancedPlayableCapabilityIds = [
  ...unsupportedAdvancedCapabilityIds,
  'filters.pixijs',
  'video.playback',
] as const;

export const PlayableBaseCapabilityIdSchema = z.enum(playableBaseCapabilityIds);
export type PlayableBaseCapabilityId = z.infer<typeof PlayableBaseCapabilityIdSchema>;

export const PlayableAdvancedCapabilityIdSchema = z.enum(advancedPlayableCapabilityIds);
export type PlayableAdvancedCapabilityId = z.infer<typeof PlayableAdvancedCapabilityIdSchema>;

export const UnsupportedPlayableCapabilityIdSchema = z.enum(unsupportedAdvancedCapabilityIds);
export type UnsupportedPlayableCapabilityId = z.infer<typeof UnsupportedPlayableCapabilityIdSchema>;

export const PlayableCapabilityEntryV1Schema = z.strictObject({
  id: PlayableBaseCapabilityIdSchema,
  title: z.string().min(1),
  description: z.string().min(1),
});
export type PlayableCapabilityEntryV1 = z.infer<typeof PlayableCapabilityEntryV1Schema>;

export const PlayableAdvancedCapabilityEntryV1Schema = z.strictObject({
  id: PlayableAdvancedCapabilityIdSchema,
  title: z.string().min(1),
  status: z.enum(['supported', 'unsupported']),
  description: z.string().min(1),
});
export type PlayableAdvancedCapabilityEntryV1 = z.infer<typeof PlayableAdvancedCapabilityEntryV1Schema>;

export const UnsupportedPlayableCapabilityV1Schema = z.strictObject({
  id: UnsupportedPlayableCapabilityIdSchema,
  title: z.string().min(1),
  fallbackGuidance: z.string().min(1),
});
export type UnsupportedPlayableCapabilityV1 = z.infer<typeof UnsupportedPlayableCapabilityV1Schema>;

export const PlayableCapabilityManifestV1Schema = z.strictObject({
  version: z.literal('playable_capabilities.v1'),
  renderer: z.literal('pixi-v8'),
  baseCapabilities: z.array(PlayableCapabilityEntryV1Schema),
  advancedCapabilities: z.array(PlayableAdvancedCapabilityEntryV1Schema),
  unsupported: z.array(UnsupportedPlayableCapabilityV1Schema),
});
export type PlayableCapabilityManifestV1 = z.infer<typeof PlayableCapabilityManifestV1Schema>;

export const defaultPlayableCapabilityManifest: PlayableCapabilityManifestV1 = {
  version: 'playable_capabilities.v1',
  renderer: 'pixi-v8',
  baseCapabilities: [
    {
      id: 'layers.container',
      title: 'Container layers',
      description: 'Create and address renderer-owned container layers through the playable API.',
    },
    {
      id: 'layers.sprite',
      title: 'Sprite layers',
      description: 'Display image-backed layers from approved project assets.',
    },
    {
      id: 'layers.text',
      title: 'Text layers',
      description: 'Display renderer-owned text layers without direct Pixi access.',
    },
    {
      id: 'input.pointer',
      title: 'Pointer input',
      description: 'Subscribe to runtime-mapped design-space pointer events.',
    },
    {
      id: 'input.tap',
      title: 'Tap input',
      description: 'Use runtime-owned tap interpretation instead of manual coordinate math.',
    },
    {
      id: 'input.swipe',
      title: 'Swipe input',
      description: 'Use runtime-owned swipe interpretation for directional gestures.',
    },
    {
      id: 'input.drag',
      title: 'Drag input',
      description: 'Use runtime-owned drag interpretation for pointer movement mechanics.',
    },
    {
      id: 'loop.raf',
      title: 'Animation frame loop',
      description: 'Run cleanup-safe frame callbacks owned by the playable runtime.',
    },
    {
      id: 'loop.delay',
      title: 'Delayed callback',
      description: 'Schedule cleanup-safe delayed callbacks through the playable runtime.',
    },
    {
      id: 'loop.interval',
      title: 'Interval callback',
      description: 'Schedule cleanup-safe repeated callbacks through the playable runtime.',
    },
    {
      id: 'tween.basic',
      title: 'Basic tweens',
      description: 'Animate supported numeric layer properties with runtime-managed cleanup.',
    },
    {
      id: 'slots.ref',
      title: 'Slot references',
      description: 'Address declared gameplay slots without depending on renderer internals.',
    },
    {
      id: 'assets.alias',
      title: 'Asset aliases',
      description: 'Load and address project assets by runtime-provided aliases.',
    },
    {
      id: 'telemetry.emit',
      title: 'Telemetry events',
      description: 'Emit gameplay telemetry through the approved playable API surface.',
    },
    {
      id: 'cta.open',
      title: 'CTA open',
      description: 'Open the configured call-to-action through the approved runtime hook.',
    },
  ],
  advancedCapabilities: [
    {
      id: 'shader.custom',
      title: 'Custom shaders',
      status: 'unsupported',
      description: 'Custom shader programs are not exposed to generated code in F4.',
    },
    {
      id: 'physics.engine',
      title: 'Physics engine',
      status: 'unsupported',
      description: 'Physics engines are not bundled or exposed to generated code in F4.',
    },
    {
      id: 'renderer.custom',
      title: 'Custom renderer',
      status: 'unsupported',
      description: 'Generated code cannot replace the managed Pixi v8 renderer in F4.',
    },
    {
      id: 'filters.pixijs',
      title: 'Pixi filters',
      status: 'unsupported',
      description: 'Raw Pixi filter access is not exposed to generated code in F4.',
    },
    {
      id: 'video.playback',
      title: 'Video playback',
      status: 'unsupported',
      description: 'Video playback helpers are not part of the F4 runtime API surface.',
    },
  ],
  unsupported: [
    {
      id: 'shader.custom',
      title: 'Custom shaders',
      fallbackGuidance: 'Use sprite, text, tween, and approved filter-like visual fallbacks instead of custom shader code.',
    },
    {
      id: 'physics.engine',
      title: 'Physics engine',
      fallbackGuidance: 'Use deterministic kinematic movement, tweens, and simple collision fallbacks instead of a physics engine.',
    },
    {
      id: 'renderer.custom',
      title: 'Custom renderer',
      fallbackGuidance: 'Use the managed pixi-v8 layer and loop capabilities as a fallback instead of a custom renderer.',
    },
  ],
};

export function getDefaultPlayableCapabilityManifest(): PlayableCapabilityManifestV1 {
  return PlayableCapabilityManifestV1Schema.parse(defaultPlayableCapabilityManifest);
}
